import { GA_CONFIG, isGAEnabled } from '../config/analytics';

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/products', '/contact')
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title) => {
  if (!isGAEnabled() || !window.gtag) {
    return;
  }

  try {
    const config = {
      page_path: path,
    };
    
    if (title) {
      config.page_title = title;
    }
    
    window.gtag('config', GA_CONFIG.measurementId, config);

    if (GA_CONFIG.debug) {
      console.log('GA Page View:', path, title || '');
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'form_submit', 'button_click')
 * @param {Object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGAEnabled() || !window.gtag) {
    return;
  }

  try {
    window.gtag('event', eventName, eventParams);

    if (GA_CONFIG.debug) {
      console.log('GA Event:', eventName, eventParams);
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Track form submission
 * @param {string} formName - Name of the form (e.g., 'contact_form')
 * @param {boolean} success - Whether the submission was successful
 */
export const trackFormSubmission = (formName, success = true) => {
  trackEvent('form_submit', {
    form_name: formName,
    success: success,
  });
};

/**
 * Track product view
 * @param {string} productName - Name of the product
 * @param {string} category - Product category
 */
export const trackProductView = (productName, category) => {
  trackEvent('view_item', {
    item_name: productName,
    item_category: category,
  });
};

/**
 * Track service view
 * @param {string} serviceName - Name of the service
 */
export const trackServiceView = (serviceName) => {
  trackEvent('view_service', {
    service_name: serviceName,
  });
};

/**
 * Track blog post view
 * @param {string} postTitle - Title of the blog post
 */
export const trackBlogView = (postTitle) => {
  trackEvent('view_blog_post', {
    post_title: postTitle,
  });
};

/**
 * Track external link click
 * @param {string} linkUrl - URL of the external link
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (linkUrl, linkText) => {
  trackEvent('click', {
    link_url: linkUrl,
    link_text: linkText,
    event_category: 'outbound',
  });
};

/**
 * Track button click
 * @param {string} buttonName - Name/identifier of the button
 * @param {string} location - Where the button is located (e.g., 'header', 'footer')
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

/**
 * Track file download
 * @param {string} fileName - Name of the file being downloaded
 * @param {string} fileType - Type/extension of the file (e.g., 'pdf', 'docx')
 * @param {string} fileLocation - Location where file was downloaded from
 */
export const trackFileDownload = (fileName, fileType, fileLocation = '') => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    file_location: fileLocation,
  });
};

/**
 * Track video play
 * @param {string} videoName - Name/identifier of the video
 * @param {string} videoLocation - Location where video is played (e.g., 'homepage', 'product-page')
 */
export const trackVideoPlay = (videoName, videoLocation = '') => {
  trackEvent('video_play', {
    video_name: videoName,
    video_location: videoLocation,
  });
};

/**
 * Track web vital metric
 * @param {Object} metric - Web vital metric object with name, value, id, delta
 */
export const trackWebVital = (metric) => {
  if (!isGAEnabled() || !window.gtag) {
    return;
  }

  try {
    // Send web vitals to GA4
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_label: metric.id,
      non_interaction: true,
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
    });

    if (GA_CONFIG.debug) {
      console.log('GA Web Vital:', metric.name, metric.value);
    }
  } catch (error) {
    console.error('Error tracking web vital:', error);
  }
};
